import type { FC, ReactNode } from 'react'
import type { DealPhase } from '@/components/landing/deal-mockup'

import { useEffect, useRef, useState } from 'react'
import { Play, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SkyPanel } from '@/components/generals/sky-panel'
import { DealMockup, TIMELINE_ROWS } from '@/components/landing/deal-mockup'
import { ButtonLink, buttonStyles } from '@/components/generals/button'

import { DEMO_HREF } from '@/constants/navigation'

const READ_MS = 1500
const ROW_MS = 450
const AUTOPLAY_DELAY_MS = 1600

const ANNOUNCEMENTS: Record<DealPhase, string> = {
    reading: 'Sample deal: Closewell is reading the contract.',
    building: 'Sample deal: Closewell is building the timeline.',
    done: 'Sample deal: timeline ready with five key dates.'
}

export const ProductPanel: FC = (): ReactNode => {
    const [phase, setPhase] = useState<DealPhase>('done')
    const [visibleRows, setVisibleRows] = useState(TIMELINE_ROWS.length)
    const [played, setPlayed] = useState(false)
    const timers = useRef<Array<number>>([])
    const sectionRef = useRef<HTMLElement>(null)
    const playRef = useRef<() => void>(() => undefined)

    useEffect(() => {
        const pending = timers.current

        return () => {
            pending.splice(0).forEach(id => window.clearTimeout(id))
        }
    }, [])

    // Play the sample deal on its own the first time the window scrolls into view
    useEffect(() => {
        const element = sectionRef.current

        if (!element || typeof IntersectionObserver === 'undefined') return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return

            observer.disconnect()
            timers.current.push(window.setTimeout(() => playRef.current(), AUTOPLAY_DELAY_MS))
        }, { threshold: 0.35 })

        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    const play = () => {
        timers.current.splice(0).forEach(id => window.clearTimeout(id))

        const at = (delay: number, run: () => void) => {
            timers.current.push(window.setTimeout(run, delay))
        }

        setPlayed(true)
        setPhase('reading')
        setVisibleRows(0)

        at(READ_MS, () => setPhase('building'))

        TIMELINE_ROWS.forEach((_, index) => {
            at(READ_MS + (index + 1) * ROW_MS, () => setVisibleRows(index + 1))
        })

        at(READ_MS + TIMELINE_ROWS.length * ROW_MS + 350, () => setPhase('done'))
    }

    playRef.current = play

    const running = phase !== 'done'

    return (
        <section id='product' ref={sectionRef} aria-labelledby='product-title' className='relative px-2 sm:px-4 lg:px-5'>
            <SkyPanel preload innerClassName='mx-auto max-w-[57rem] px-4 pt-8 pb-8 sm:px-8 sm:pt-12 sm:pb-10'>
                <div className='animate-in delay-300 duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] fill-mode-both slide-in-from-bottom-24 zoom-in-95'>
                    <DealMockup phase={phase} visibleRows={visibleRows} />
                </div>

                <div className='reveal mt-7 flex flex-col gap-5 sm:mt-8 sm:flex-row sm:items-center sm:justify-between'>
                    <div className='max-w-sm'>
                        <h2 id='product-title' className='text-[15px] font-medium text-foreground'>
                            Want to see it read a real contract?
                        </h2>

                        <p className='mt-1 text-[13px] leading-snug text-foreground/65'>
                            Play the sample deal and watch Closewell turn 32 pages into a timeline every party can follow.
                        </p>
                    </div>

                    <div className='flex flex-col gap-2 sm:flex-row'>
                        <button
                            type='button'
                            onClick={play}
                            disabled={running}
                            className={buttonStyles({ size: 'sm', className: 'min-w-[13.5rem]' })}
                        >
                            {played && !running ? <RotateCcw className='size-4' /> : <Play className={cn('size-4', running && 'soft-pulse')} />}
                            {running ? 'Reading the contract' : played ? 'Replay the sample deal' : 'Play the sample deal'}
                        </button>

                        <ButtonLink href={DEMO_HREF} variant='light' size='sm'>
                            Book a walkthrough
                        </ButtonLink>
                    </div>
                </div>

                <p aria-live='polite' className='sr-only'>
                    {played ? ANNOUNCEMENTS[phase] : ''}
                </p>
            </SkyPanel>
        </section>
    )
}
